import { config } from '../config';
import type { Issue } from './api/DigiobcanApi';
import { format, type DateArg } from 'date-fns';
import { sk } from 'date-fns/locale';
import { load as getGps } from 'exifreader';
import {
  closeSnackbar,
  enqueueSnackbar,
  type OptionsObject,
  type SnackbarKey,
  type SnackbarMessage,
} from 'notistack';
import React from 'react';

const toastProperties = {
  autoHideDuration: 3000,
  preventDuplicate: true,
  anchorOrigin: { horizontal: 'right', vertical: 'top' },
  style: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 8,
  },
} as OptionsObject;

export const getIssueStatusLabel = (categoryId: Issue['status']) => {
  switch (categoryId) {
    case 'accepted':
      return __('V riešení');
    case 'moved':
      return __('Presunuté');
    case 'open':
      return __('Podané');
    case 'duplicate':
      return __('Duplikát');
    case 'resolved':
      return __('Vyriešené');
    default:
      return __('Neznámy stav');
  }
};

export const __ = (text: string) => {
  return text;
};

export const formatDate = (date: Date) => {
  return format(date, 'd.M.yyyy', { locale: sk });
};

export const formatDateTime = (date: DateArg<Date>) => {
  return format(date, 'd.M.yyyy H:mm', { locale: sk });
};

export const formatPoints = (points: number, suffix?: string) => {
  const absPoints = Math.abs(points);
  const sign = points < 0 ? '-' : '';

  const formattedNumber =
    absPoints > 999
      ? `${sign}${(absPoints / 1000).toFixed(absPoints % 1000 === 0 ? 0 : 1)}k`
      : `${points}`;

  return suffix ? `${formattedNumber} ${suffix}` : formattedNumber;
};

export const formatNotificationDate = (date: Date | string) => {
  const inputDate = new Date(date);
  const now = new Date();
  const diffInMinutes = (now.getTime() - inputDate.getTime()) / (1000 * 60);

  if (diffInMinutes < 1) {
    return __('teraz');
  }

  if (diffInMinutes < 60) {
    return `${__('pred')} ${Math.floor(diffInMinutes)}min`;
  }

  const diffInHours = diffInMinutes / 60;
  if (diffInHours < 12) {
    return `${__('pred')} ${Math.floor(diffInHours)}h`;
  }

  return formatDate(inputDate);
};

export const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return new Uint8Array([...raw].map((char) => char.charCodeAt(0)));
};

export const hasLocation = (newItem?: {
  location?: { coordinates?: number[] };
}) => {
  return (
    newItem?.location?.coordinates?.[0] && newItem?.location?.coordinates?.[1]
  );
};

export function useDebounced<T>(value: T, interval: number): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), interval);
    return () => clearTimeout(timeout);
  }, [interval, value]);

  return debounced;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fileAcceptTypes = [
  // Any files
  '*/*',

  // Images
  'image/*',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'image/heic',

  // Videos
  'video/*',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/avi',
  'video/mov',
  'video/quicktime',

  // Documents
  'application/pdf',
] as const;

export type FileAcceptType = (typeof fileAcceptTypes)[number];

/**
 * This function is used to generate a dummy array of a given length;
 *
 * Example:
 * dummyArray(3) => [0, 1, 2]
 */
export const dummyArray = (length: number) => {
  return Array.from({ length }, (_, i) => i);
};

export const slugifyToPascalCase = (input) => {
  return input
    .normalize('NFD') // rozloží znaky s diakritikou (ä → a + ¨)
    .replace(/[\u0300-\u036f]/g, '') // odstráni diakritiku
    .replace(/[^a-zA-Z0-9\s]/g, '') // odstráni špeciálne znaky okrem písmen, číslic a medzier
    .toLowerCase()
    .split(/\s+/) // rozdelí podľa medzier
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const required = (value?: any): string | null => {
  if (typeof value === 'undefined') {
    return __('Pole je povinné');
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return __('Pole je povinné');
  }
  if (value || value === 0) {
    return null;
  }
  return __('Pole je povinné');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const requiredArray = (value?: any[]): string | null => {
  if (!Array.isArray(value) || value.length === 0) {
    return __('Pole musí obsahovať aspoň jeden prvok');
  }

  return null;
};

export const isLink = (url?: string): string | null => {
  if (typeof url === 'undefined') {
    return __('Pole je povinné');
  }
  if (url && /^https?:\/\/.+/.test(url)) {
    return null;
  }
  return __('Neplatný odkaz');
};

// validation functions
export const validateMaxFileSize = (
  files: File[] = [],
  maxFileSize: number,
): string | null => {
  const hasLargeFile = files.some(
    (file) => file.size > maxFileSize * 1024 * 1024,
  );
  if (hasLargeFile) {
    return `Maximálna veľkosť súboru je ${maxFileSize} MB.`;
  }
  return null;
};

export const noSpaces = (value?: string | number): string | null => {
  if (typeof value === 'undefined') {
    return __('Pole je povinné');
  }
  if (typeof value === 'string' && value && /\s/.test(value)) {
    return __('Pole nesmie obsahovať medzery');
  }
  if (value || value === 0) {
    return null;
  }
  return __('Pole je povinné');
};

export const toastSuccess = (message: SnackbarMessage) => {
  return enqueueSnackbar(message, {
    variant: 'success',
    ...toastProperties,
  });
};

export const toastError = (message: SnackbarMessage) => {
  return enqueueSnackbar(message, {
    variant: 'error',
    ...toastProperties,
  });
};

export const toastWarning = (
  message: SnackbarMessage,
  permanent: boolean = false,
) => {
  return enqueueSnackbar(message, {
    variant: 'warning',
    persist: permanent,
    ...toastProperties,
  });
};

export const closeToast = (key: SnackbarKey) => {
  closeSnackbar(key);
};

export const toastInfo = (message: SnackbarMessage) => {
  return enqueueSnackbar(message, {
    variant: 'info',
    ...toastProperties,
  });
};

export const copyToClipboard = (
  value: string,
  callback?: { onSuccess?: () => void },
) => {
  if (navigator.clipboard) {
    try {
      navigator.clipboard.writeText(value);
      callback?.onSuccess?.();
    } catch (err) {
      toastError(__('Chyba pri kopírovaní do schránky'));
      console.error('Clipboard API error:', err);
    }
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      callback?.onSuccess?.();
    } catch (err) {
      toastError(__('Chyba pri kopírovaní do schránky'));
      console.error('Fallback copy error:', err);
    }
    document.body.removeChild(textarea);
  }
};

const URN_ID_REGEX = /^[A-Z][a-zA-Z0-9]*(?::[A-Z][a-zA-Z0-9]*)*:\S+$/;

// Hierarchical URN prefixes - try specific first, then fallback to general
const URN_PREFIXES = [
  'urn:ngsi-ld:presov.sk:digital-obcan', // Specific prefix (from config)
  'urn:ngsi-ld:presov.sk', // General fallback prefix
];

export function isUrn(value: string): boolean {
  return URN_PREFIXES.some((prefix) => value.startsWith(`${prefix}:`));
}

export function toUrn(value: string): string {
  if (isUrn(value)) return value;
  if (URN_ID_REGEX.test(value)) {
    return `${config.URN_PREFIX}:${value}`;
  }
  return value;
}

export function fromUrn(value: string): string {
  // Try specific prefix first, then fallback to general prefix
  for (const prefix of URN_PREFIXES) {
    if (value.startsWith(`${prefix}:`)) {
      return value.replace(`${prefix}:`, '');
    }
  }
  return value;
}

export function composeValidators<T>(
  ...validators: ((value: T) => string | null)[]
) {
  return (value: T) =>
    validators.reduce<string | null>(
      (error, validator) => error || validator(value),
      null,
    );
}

const mediaTypePattern =
  /^(?<type>[^/]+)\/(?<subtype>[^;]+)(;(?<parameters>.*))?$/;
const parseMediaType = (
  mediaType: string,
):
  | {
      type: string | undefined;
      subtype: string | undefined;
      parameters: [string, string][];
    }
  | undefined => {
  const res = mediaTypePattern.exec(mediaType);
  if (!res) return;
  const { type, subtype, parameters = '' } = res.groups;
  const parsedParameters: [string, string][] = [];
  for (const parameter of parameters.split(';').filter((p) => p.length)) {
    const parsed = parameter.split('=', 2);
    if (parsed.length !== 2) return;
    else parsedParameters.push(parsed as [string, string]);
  }
  return {
    type: type === '*' ? undefined : type.toLowerCase(),
    subtype: subtype === '*' ? undefined : subtype.toLowerCase(),
    parameters: parsedParameters,
  };
};

/**
 * Vie appka prílohu zobraziť sama, alebo ju má odovzdať prehliadaču?
 *
 * API neodmieta žiadny typ prílohy, takže v podnete môže byť zvuk, PDF aj
 * dokument. Náhľad, karusel a lightbox stavajú na `<img>` a `<video>` - čokoľvek
 * iné v nich skončí ako prázdny prehrávač, tak sa to otvára v novej karte.
 */
export const isPreviewableMedia = (mediaType?: string | null) =>
  !!mediaType &&
  (mediaType.startsWith('image/') || mediaType.startsWith('video/'));

export const isSupportedMediaType = (
  supportedTypes: string[],
  type: string,
): boolean => {
  const parsed = parseMediaType(type);
  if (!parsed || !parsed.type || !parsed.subtype) return false;
  const parsedParameters = new Map(parsed.parameters);
  for (const supported of supportedTypes) {
    const parsedSupported = parseMediaType(supported);
    if (!parsedSupported) {
      throw new Error(`could not parse supported media type '${supported}'`);
    }
    const { type, subtype, parameters } = parsedSupported;
    if (
      (!type || type === parsed.type) &&
      (!subtype || subtype === parsed.subtype) &&
      (!parameters.length ||
        parameters.every(([k, v]) => parsedParameters.get(k) === v))
    ) {
      return true;
    }
  }
  return false;
};

export const arrayDiff = <T>(
  oldItems: T[],
  newItems: T[],
  itemKey: (item: T) => unknown,
): {
  deleted: T[];
  added: T[];
} => {
  const added: T[] = [];
  const oldMap = new Map(oldItems.map((item) => [itemKey(item), item]));

  for (const item of newItems) {
    const key = itemKey(item);
    if (oldMap.has(key)) {
      oldMap.delete(key);
    } else {
      added.push(item);
    }
  }

  return { deleted: [...oldMap.values()], added };
};
const PLURAL_SK = new Intl.PluralRules('sk');
const OPTIONS_PLURAL_SK: Record<Intl.LDMLPluralRule, string> = {
  zero: 'možností',
  one: 'možnosť',
  two: 'možnosti',
  few: 'možnosti',
  many: 'možností',
  other: 'možností',
};

export const limitsTip = (min: number, max: number, total: number) => {
  const limits =
    min === max
      ? `${min} ${OPTIONS_PLURAL_SK[PLURAL_SK.select(min)]}`
      : [
          min > 0
            ? `${__('aspoň')} ${min} ${OPTIONS_PLURAL_SK[PLURAL_SK.select(min)]}`
            : '',
          max < total
            ? `${__('maximálne')} ${max} ${OPTIONS_PLURAL_SK[PLURAL_SK.select(max)]}`
            : '',
        ]
          .filter((l) => !!l)
          .join(', ');
  return `${__('Vyberte')} ${limits}.`;
};

export const getLocationMetadataFromMedia = async (files: File[]) => {
  for (const file of files) {
    // exifreader číta len obrázky - na videu by hodil 'Invalid image format'.
    if (!file.type.startsWith('image/')) {
      continue;
    }

    // try je vnútri cyklu zámerne: keď sa jeden súbor nedá prečítať,
    // musíme skúsiť ďalšie, inak by sme prišli o GPS z nasledujúcej fotky.
    try {
      const tags = await getGps(file);

      if (
        tags.GPSLongitude.description &&
        !isNaN(+tags.GPSLongitude.description) &&
        tags.GPSLatitude.description &&
        !isNaN(+tags.GPSLatitude.description)
      ) {
        return {
          longitude: Number(tags.GPSLongitude.description),
          latitude: Number(tags.GPSLatitude.description),
        };
      }
    } catch (error) {
      console.error('Error getting location metadata from media:', error);
    }
  }

  return null;
};

export const getVotesDeclension = (count: number): string =>
  declension(count, ['hlas', 'hlasy', 'hlasov']);

/**
 * Slovenské skloňovanie po číslovke: 1 kategória, 2-4 kategórie, 5+ kategórií.
 * Bez toho vychádzali v hlavičkách zoznamov tvary ako „3 kategórií".
 */
export const declension = (
  count: number,
  [one, few, many]: [string, string, string],
): string => {
  if (count === 1) return one;
  if (count >= 2 && count <= 4) return few;
  return many;
};

export const validateDistance = (value: string | undefined) => {
  if (!value) return undefined;

  const trimmed = value.trim();
  const match = trimmed.match(/^(\d+)\s*(km|m)?$/i);

  if (!match) {
    return __(
      'Neplatný formát. Použite celé číslo s jednotkou (napr. 5 km alebo 500 m)',
    );
  }

  return undefined;
};

/**
 * List of tracking parameters that should be removed from URLs.
 * These are typically added by social media platforms, advertising networks, and analytics tools.
 */
const TRACKING_PARAMS = [
  // Facebook / Meta
  'fbclid',
  'fb_action_ids',
  'fb_action_types',
  'fb_source',
  'fb_ref',
  // Google
  'gclid',
  'gclsrc',
  'dclid',
  // Microsoft / Bing
  'msclkid',
  // Twitter / X
  'twclid',
  // TikTok
  'ttclid',
  // LinkedIn
  'li_fat_id',
  // Other common tracking params
  'brid',
  'mc_cid',
  'mc_eid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  '_ga',
  '_gl',
  'ref',
  'ref_src',
  'ref_url',
  // Instagram
  'igshid',
  'igsh',
  // Snapchat
  'scid',
  // Pinterest
  'epik',
];

/**
 * Removes tracking parameters from a URL search string.
 * @param search - The search string (e.g., "?foo=bar&fbclid=xxx")
 * @returns Clean search string without tracking parameters
 */
export const cleanUrlSearch = (search: string): string => {
  if (!search) return '';

  const params = new URLSearchParams(search);
  const trackingParamsLower = TRACKING_PARAMS.map((p) => p.toLowerCase());

  // Create a list of keys to delete (to avoid modifying during iteration)
  const keysToDelete: string[] = [];
  params.forEach((_, key) => {
    if (trackingParamsLower.includes(key.toLowerCase())) {
      keysToDelete.push(key);
    }
  });

  keysToDelete.forEach((key) => params.delete(key));

  const result = params.toString();
  return result ? `?${result}` : '';
};

/**
 * Builds a clean canonical URL from pathname, search, and hash.
 * Removes tracking parameters from the search string.
 * @param pathname - The pathname (e.g., "/podnety/pdnt:24345GR")
 * @param search - The search string (e.g., "?fbclid=xxx")
 * @param hash - The hash (e.g., "#section")
 * @param origin - Optional origin (defaults to window.location.origin)
 * @returns Clean full URL
 */
export const buildCleanUrl = (
  pathname: string,
  search: string = '',
  hash: string = '',
  origin?: string,
): string => {
  const cleanSearch = cleanUrlSearch(search);
  const path = `${pathname ?? ''}${cleanSearch}${hash ?? ''}`;

  if (origin) {
    return `${origin}${path}`;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }

  return path;
};
